import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate, statusColor, initials } from "@/lib/utils-format";
import { Plus, Trash2, Building2, Phone, Mail, MapPin, FileText, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", company: "", email: "", phone: "", gstin: "",
  address_line1: "", address_line2: "", city: "", state: "", pincode: "",
  customer_type: "retail", credit_limit: 0, notes: "",
};

const TYPES = ["retail", "wholesale", "corporate", "horeca"];

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    const { data } = await api.get("/customers");
    setCustomers(data);
    if (!selectedId && data.length > 0) setSelectedId(data[0].id);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedId) return setDetail(null);
    api.get(`/customers/${selectedId}`).then((r) => setDetail(r.data));
  }, [selectedId]);

  const submit = async () => {
    try {
      const payload = { ...form, credit_limit: Number(form.credit_limit) || 0 };
      if (editingId) await api.patch(`/customers/${editingId}`, payload);
      else await api.post("/customers", payload);
      toast.success(editingId ? "Customer updated" : "Customer created");
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openEdit = () => {
    if (!detail) return;
    setForm({ ...empty, ...detail, credit_limit: detail.credit_limit || 0 });
    setEditingId(detail.id);
    setOpen(true);
  };

  const remove = async () => {
    if (!detail) return;
    if (!window.confirm(`Delete customer ${detail.name}?`)) return;
    await api.delete(`/customers/${detail.id}`);
    toast.success("Deleted");
    setSelectedId(null);
    load();
  };

  const filtered = customers.filter((c) => {
    const s = search.toLowerCase();
    return !s || [c.name, c.company, c.email, c.phone, c.city].some((f) => (f || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Contacts</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            Customers
          </h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="customer-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader><DialogTitle className="font-[Outfit]">{editingId ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2 max-h-[70vh] overflow-y-auto">
              <div><Label>Name *</Label><Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Address Line 1</Label><Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address Line 2</Label><Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} /></div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><Label>Pincode</Label><Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
              <div><Label>Credit Limit (₹)</Label><Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="customer-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 min-h-[70vh]">
        {/* List */}
        <div className="lg:col-span-1 bg-white border border-[#EAE5D9] rounded-lg flex flex-col">
          <div className="p-3 border-b border-[#EAE5D9]">
            <Input
              placeholder="Search customers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="customer-search"
              className="h-9"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={c.id}
                data-testid={`customer-list-item-${c.id}`}
                onClick={() => setSelectedId(c.id)}
                className={`w-full text-left px-4 py-3 border-b border-[#EAE5D9] transition-colors flex items-center gap-3 ${
                  c.id === selectedId ? "bg-[#FEEBC8]" : "hover:bg-[#FDFBF7]"
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-[#C05621] text-white flex items-center justify-center text-xs font-semibold shrink-0">
                  {initials(c.name)}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{c.name}</div>
                  <div className="text-xs text-[#8B7355] truncate">{c.company || c.phone || c.email || c.city}</div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && <div className="text-sm text-[#8B7355] text-center py-8">No customers</div>}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 bg-white border border-[#EAE5D9] rounded-lg" data-testid="customer-detail-pane">
          {!detail ? (
            <div className="text-sm text-[#8B7355] p-8 text-center">Select a customer to view details</div>
          ) : (
            <>
              <div className="p-6 border-b border-[#EAE5D9] flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#C05621] text-white flex items-center justify-center font-semibold text-lg">
                    {initials(detail.name)}
                  </div>
                  <div>
                    <h2 className="font-[Outfit] text-2xl font-semibold tracking-tight">{detail.name}</h2>
                    <div className="flex items-center gap-2 text-sm text-[#5C4033] mt-1">
                      {detail.company && <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {detail.company}</span>}
                      <Badge variant="outline" className="capitalize">{detail.customer_type}</Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={openEdit} data-testid="customer-edit-btn">Edit</Button>
                  <Button variant="outline" onClick={remove} data-testid="customer-delete-btn" className="text-red-600 hover:text-red-700 border-red-300">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <Tabs defaultValue="contact" className="p-6">
                <TabsList className="bg-[#F7F2E8]">
                  <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
                  <TabsTrigger value="history" data-testid="tab-history">Purchase History ({detail.sales_docs?.length || 0})</TabsTrigger>
                  <TabsTrigger value="comms" data-testid="tab-communication">Communication ({detail.followups?.length || 0})</TabsTrigger>
                </TabsList>

                <TabsContent value="contact" className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoRow icon={Mail} label="Email" value={detail.email} />
                  <InfoRow icon={Phone} label="Phone" value={detail.phone} />
                  <InfoRow icon={FileText} label="GSTIN" value={detail.gstin} />
                  <InfoRow icon={MapPin} label="City / State" value={[detail.city, detail.state].filter(Boolean).join(", ")} />
                  <InfoRow icon={MapPin} label="Address" value={[detail.address_line1, detail.address_line2, detail.pincode].filter(Boolean).join(", ")} />
                  <InfoRow icon={FileText} label="Credit Limit" value={inr(detail.credit_limit || 0)} />
                  {detail.notes && (
                    <div className="col-span-2">
                      <div className="uc-label mb-1">Notes</div>
                      <p className="text-sm text-[#2C1810] whitespace-pre-wrap">{detail.notes}</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="history" className="pt-6">
                  {detail.sales_docs?.length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left uc-label border-b border-[#EAE5D9]">
                            <th className="py-2">Document</th>
                            <th className="py-2">Type</th>
                            <th className="py-2">Status</th>
                            <th className="py-2 text-right">Total</th>
                            <th className="py-2">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.sales_docs.map((d) => (
                            <tr key={d.id} className="border-b border-[#EAE5D9] last:border-0">
                              <td className="py-2.5 font-medium">{d.number}</td>
                              <td className="py-2.5 capitalize">{d.doc_type}</td>
                              <td className="py-2.5"><Badge variant="outline" className={`capitalize ${statusColor(d.status)}`}>{d.status}</Badge></td>
                              <td className="py-2.5 text-right">{inr(d.grand_total)}</td>
                              <td className="py-2.5 text-[#8B7355]">{fmtDate(d.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-[#8B7355]">No sales history yet.</p>
                  )}
                </TabsContent>

                <TabsContent value="comms" className="pt-6">
                  {detail.followups?.length ? (
                    <div className="space-y-2">
                      {detail.followups.map((f) => (
                        <div key={f.id} className="flex items-start gap-3 p-3 border border-[#EAE5D9] rounded-md">
                          <CalendarClock className="w-4 h-4 text-[#C05621] mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium capitalize">{f.type}</span>
                              <Badge variant="outline" className={`capitalize ${statusColor(f.status)}`}>{f.status}</Badge>
                              <span className="text-[#8B7355] text-xs ml-auto">{fmtDate(f.scheduled_at)}</span>
                            </div>
                            {f.notes && <p className="text-sm text-[#5C4033] mt-1">{f.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#8B7355]">No communications yet.</p>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-md bg-[#FEEBC8] text-[#C05621] flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <div className="uc-label">{label}</div>
      <div className="text-sm text-[#2C1810] mt-0.5">{value || "—"}</div>
    </div>
  </div>
);
